import React, { useState, useEffect } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTheme: any;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, currentTheme }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    organizationType: '',
    projectType: '',
    message: ''
  });

  const [isVisible, setIsVisible] = useState(false);

  // Handle smooth transitions
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen && !isVisible) return null;

  const modalStyle = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
    transform: isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-20px)',
    opacity: isOpen ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const backdropStyle = {
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const inputStyle = {
    background: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '16px',
    transition: 'all 0.3s ease'
  };

  const btnStyle = {
    background: currentTheme.brand,
    color: currentTheme.text,
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer'
  };

  const dividerStyle = {
    background: 'linear-gradient(to right, transparent, var(--brand), transparent)',
    height: '1px',
    margin: '24px 0',
    opacity: 0.3
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        style={backdropStyle}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl p-8" style={modalStyle}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl transition-colors duration-200"
        >
          ×
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: currentTheme.brand }}>
            Start Your Strategy
          </h2>
          <p className="text-gray-600">
            Let's discuss how IKON Consulting can transform your organization's impact.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: currentTheme.brand }}>
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Company/Organization
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={dividerStyle}></div>

          {/* Organization Details Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: currentTheme.brand }}>
              Organization Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Organization Type
                </label>
                <select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                >
                  <option value="">Select type...</option>
                  <option value="nonprofit">Nonprofit</option>
                  <option value="educational">Educational Institution</option>
                  <option value="foundation">Foundation</option>
                  <option value="government">Government Agency</option>
                  <option value="corporate">Corporate</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                  Project Type
                </label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  style={inputStyle}
                  className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50"
                >
                  <option value="">Select project...</option>
                  <option value="strategic-planning">Strategic Planning</option>
                  <option value="partnership-development">Partnership Development</option>
                  <option value="revenue-optimization">Revenue Optimization</option>
                  <option value="organizational-development">Organizational Development</option>
                  <option value="consultation">General Consultation</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={dividerStyle}></div>

          {/* Project Description Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: currentTheme.brand }}>
              Project Description
            </h3>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                Tell us about your project *
              </label>
              <textarea
                name="message"
                required
                rows={4}
                value={formData.message}
                onChange={handleChange}
                style={inputStyle}
                className="w-full focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none"
                placeholder="Describe your organization's goals, challenges, and how we can help..."
              />
            </div>
          </div>

          {/* Divider */}
          <div style={dividerStyle}></div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              className="px-8 py-3 text-lg font-semibold hover:transform hover:-translate-y-0.5 hover:shadow-lg transition-all"
              style={btnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px var(--glow)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
              }}
            >
              Submit Consultation Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
